import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { program } from "@/api";
import http from "@/api";
import { PILLARS } from "@/constants";
import { ArrowLeft, Calendar, Eye, Save, Lock, Unlock, Award } from "lucide-react";
import CategoriesManager from "@/components/CategoriesManager";

const PERIOD_OPTIONS = [
  { key: "monthly",    label: "Mensual" },
  { key: "quarterly",  label: "Trimestral" },
  { key: "semiannual", label: "Semestral" },
  { key: "annual",     label: "Anual" },
];

export default function ProgramSettings() {
  const [periodicities, setPeriodicities] = useState(null);
  const [readonly, setReadonly] = useState(null);
  const [scoreBadge, setScoreBadge] = useState(null);
  const [pilotCats, setPilotCats] = useState(null);
  const [allCats, setAllCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const [p, r, sb, pc] = await Promise.all([
      program.periodicities(), program.supplierReadonly(), program.scoreBadge(),
      http.get("/pilot-categories").then((x) => x.data),
    ]);
    setPeriodicities(p.periodicities);
    setReadonly(r.enabled);
    setScoreBadge(sb);
    setPilotCats(pc.categories);
    setAllCats(pc.all);
  };
  useEffect(() => { load(); }, []);

  const togglePilotCat = (c) => {
    setPilotCats(pilotCats.includes(c) ? pilotCats.filter((x) => x !== c) : [...pilotCats, c]);
  };
  const savePilotCats = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await http.put("/admin/pilot-categories", { categories: pilotCats });
      setMsg("Alcance del piloto guardado. Dashboards y reconocimiento consideran solo estas categorías.");
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const setPillarPeriod = (key, value) => {
    setPeriodicities({ ...periodicities, [key]: value });
  };

  const savePeriodicities = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await program.setPeriodicities(periodicities);
      setMsg("Periodicidades guardadas.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggleReadonly = async () => {
    const next = !readonly;
    const confirm = next
      ? "¿Activar Modo Visor? Los proveedores NO podrán subir documentos ni responder cuestionarios — Lala los alimenta directamente."
      : "¿Desactivar Modo Visor? Los proveedores recuperarán todas las capacidades de escritura (Fase 2).";
    if (!window.confirm(confirm)) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      await program.setSupplierReadonly(next);
      setReadonly(next);
      setMsg(next ? "Modo Visor activado." : "Modo Visor desactivado.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const saveScoreBadge = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const saved = await program.setScoreBadge({
        enabled: scoreBadge.enabled,
        label: scoreBadge.label,
        min_score: Number(scoreBadge.min_score),
      });
      setScoreBadge(saved);
      setMsg("Distintivo guardado.");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!periodicities) return <div className="px-6 py-20 text-center font-hand text-2xl text-[var(--lala-blue)]">Cargando…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="program-settings-page">
      <Link to="/admin/configuracion" className="text-sm text-[var(--lala-blue)] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Volver a configuración
      </Link>
      <div className="mb-6">
        <div className="font-hand text-xl text-[var(--lala-blue)]">programa · fase y periodicidad</div>
        <h1 className="font-display text-4xl">Ajustes del programa</h1>
        <p className="text-[var(--ink-soft)] mt-1 max-w-2xl">
          Define con qué frecuencia se evalúa cada pilar y controla si el proveedor ve la plataforma en modo lectura (Fase 1) o si ya puede subir información (Fase 2).
        </p>
      </div>

      {/* Alcance del piloto */}
      <section className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="pilot-scope-section">
        <div className="font-hand text-xl text-[var(--lala-blue)]">alcance del piloto · materiales directos</div>
        <h3 className="font-display text-2xl mb-1">Categorías activas del piloto</h3>
        <p className="text-sm text-[var(--ink-soft)] mb-4">
          Solo los proveedores de estas categorías participan en el piloto (dashboards, heatmap y reconocimiento).
          Los demás quedan visibles pero marcados "Fuera del piloto" — reactivarlos es un clic.
        </p>
        {pilotCats && (
          <div className="flex flex-wrap gap-2">
            {allCats.map((c) => (
              <button key={c} onClick={() => togglePilotCat(c)} data-testid={`pilot-cat-${c}`}
                className={`sketch-pill px-3 py-1.5 text-sm font-semibold border transition-colors ${pilotCats.includes(c) ? "bg-[var(--lala-blue)] text-[var(--cream)] border-[var(--lala-blue)]" : "bg-[var(--cream)] text-[var(--ink-soft)] border-[var(--line)]"}`}>
                {pilotCats.includes(c) ? "✓ " : ""}{c}
              </button>
            ))}
          </div>
        )}
        <button onClick={savePilotCats} disabled={busy || !pilotCats?.length} className="btn-primary text-sm mt-4" data-testid="save-pilot-cats-btn">
          <Save size={14} /> Guardar alcance
        </button>
      </section>

      {/* Phase 1 / Read-only */}
      <section className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="readonly-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
              {readonly ? <Lock size={18} /> : <Unlock size={18} />} modo proveedor
            </div>
            <h2 className="font-display text-2xl mt-1">
              {readonly ? "Fase 1 · Modo Visor" : "Fase 2 · Modo Activo"}
            </h2>
            <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-xl leading-relaxed">
              {readonly ? (
                <>
                  El proveedor <strong>solo ve</strong> su scorecard, sello digital y reportes.
                  No puede subir documentos ni responder cuestionarios — Lala alimenta toda la
                  información para evitar que sean "juez y parte". <span className="font-hand text-base text-[var(--lala-blue)]"> ideal para los primeros 60 días del programa.</span>
                </>
              ) : (
                <>
                  El proveedor puede subir documentos, responder cuestionarios y participar
                  activamente. Lala revisa, valida y publica. <span className="font-hand text-base text-[var(--lala-blue)]"> recomendado a partir del día 60.</span>
                </>
              )}
            </p>
          </div>
          <button
            onClick={toggleReadonly}
            disabled={busy}
            data-testid="toggle-readonly-btn"
            className={`sketch-pill px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 ${readonly ? "bg-[var(--lala-red)] text-[var(--cream)]" : "bg-[var(--lala-green)] text-[var(--cream)]"}`}
          >
            {readonly ? <Eye size={14} /> : <Unlock size={14} />}
            {readonly ? "Pasar a Fase 2 (Activo)" : "Volver a Fase 1 (Visor)"}
          </button>
        </div>
      </section>

      {/* Pillar periodicities */}
      <CategoriesManager />

      <section className="sketch-border bg-[var(--cream-soft)] p-6 mb-6" data-testid="periodicities-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
              <Calendar size={18} /> periodicidad por pilar
            </div>
            <h2 className="font-display text-2xl mt-1">Cadencia de evaluación</h2>
            <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-xl">
              Algunos pilares se miden cada mes (Calidad, Costo), otros una vez al año (Innovación).
              Ajusta la cadencia según tu programa.
            </p>
          </div>
          <button onClick={savePeriodicities} disabled={busy} className="btn-primary text-sm" data-testid="save-periodicities-btn">
            <Save size={14} /> {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          {PILLARS.map((p) => (
            <div key={p.key} className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4" data-testid={`periodicity-row-${p.key}`}>
              <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold">{p.label}</div>
              <select
                value={periodicities[p.key] || "monthly"}
                onChange={(e) => setPillarPeriod(p.key, e.target.value)}
                className="input-field mt-2"
                data-testid={`periodicity-select-${p.key}`}
              >
                {PERIOD_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </div>
          ))}
        </div>
      </section>

      {/* Distintivo "Mejor de su Categoría" */}
      {scoreBadge && (
        <section className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="score-badge-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
                <Award size={18} /> distintivo por score total
              </div>
              <h2 className="font-display text-2xl mt-1">"{scoreBadge.label}"</h2>
              <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-xl leading-relaxed">
                Reconocimiento adicional al nivel: se otorga automáticamente al <strong>N° 1 de cada categoría</strong>
                {" "}cuyo score global supere el mínimo configurado. Visible en el portal del proveedor, el reporte PDF,
                el dashboard y la vista del comprador.
              </p>
            </div>
            <button onClick={saveScoreBadge} disabled={busy} className="btn-primary text-sm" data-testid="save-score-badge-btn">
              <Save size={14} /> {busy ? "Guardando…" : "Guardar"}
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <div className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold mb-2">Estado</div>
              <button
                onClick={() => setScoreBadge({ ...scoreBadge, enabled: !scoreBadge.enabled })}
                data-testid="score-badge-toggle"
                className={`sketch-pill px-4 py-2 text-sm font-semibold ${scoreBadge.enabled ? "bg-[var(--lala-green)] text-white" : "bg-[var(--cream-deep)] text-[var(--ink-soft)]"}`}
              >
                {scoreBadge.enabled ? "Activado" : "Desactivado"}
              </button>
            </div>
            <div className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold">Nombre del distintivo</div>
              <input
                type="text"
                value={scoreBadge.label}
                maxLength={60}
                onChange={(e) => setScoreBadge({ ...scoreBadge, label: e.target.value })}
                className="input-field mt-2"
                data-testid="score-badge-label-input"
              />
            </div>
            <div className="bg-[var(--cream)] border border-[var(--line)] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[var(--lala-blue)] font-bold">Score mínimo (0–10)</div>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={scoreBadge.min_score}
                onChange={(e) => setScoreBadge({ ...scoreBadge, min_score: e.target.value })}
                className="input-field mt-2"
                data-testid="score-badge-minscore-input"
              />
              <p className="text-[11px] text-[var(--ink-soft)] mt-1">Equivale a {(Number(scoreBadge.min_score || 0) * 10).toFixed(0)} /100</p>
            </div>
          </div>
        </section>
      )}

      {msg && <div className="mt-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="settings-msg">{msg}</div>}
      {err && <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="settings-err">{err}</div>}
    </div>
  );
}
